// 2) Third-party library imports
import { get, isEmpty } from 'lodash-es';
import { all, call, delay, fork, put, takeEvery } from 'redux-saga/effects';

// 3a) Constants
import { API_NAME, maxAPIRefetchCount, refetchAPIDelay } from '../../config/config';

// 3c) Project-specific modules - components (and other project modules)
import { _queries as queries } from '../../graphql/queries.graphql';

// 3d) Utility functions
import { checkShouldRequestRefetch } from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import client, { post } from 'src/api';

// 3e) Redux actions
import {
    fetchCommunicationResourceResponseDetailsAction,
    publicAttachToResourceRequestAction,
    sendCommunicationResourceResponseAction,
} from './conversationLineSlice';

let refetchCount = 0;

/**
 * Function for getting the data needed to populate the details in the Customer Response page from API.
 * @param param0
 */
function* handleFetchCommunicationResourceResponseDetails({ payload: sagaPayload }: any) {
    const {
        conversationLineId,
        callback,
    } = sagaPayload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_COMMUNICATION_RESOURCE_RESPONSE_DETAILS, 
                variables: {
                    ConversationLineId: conversationLineId || null,
                }
            }
        );

        res.IsSuccess = true;
        refetchCount = 0;

        if (callback) {
            callback(res);
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(
                fetchCommunicationResourceResponseDetailsAction(
                    conversationLineId,
                    callback
                )
            );
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
            //if (callback) {
            //    const returnData = get(err.response, 'data')
            //        ? err.response.data
            //        : { Messages: [err.message] };
            //    returnData.IsSuccess = false;
            //    callback(returnData);
            //}
        }
    }
}

/**
 * Function called when sending email response.
 * @param param0
 */
 function* handleSendCommunicationResourceResponseRequest({ payload: sagaPayload }: any) {
     const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/communication/resource/emailresponse',
            options: {
                body: payload
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();
        if (callback) {
            if (isEmpty(get(res, 'Messages'))) {
                const response = {
                    ...res,
                    IsSuccess: true,
                };

                callback(response);
            }
            else {
                throw new Error('Failed');
            }
        }
    }
    catch (err) {
        yield put(publicAttachToResourceRequestAction(payload, callback));

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}
/**
 * Function called when uploading an attachment file.
 * @param param0
 */
 function* handlePublicAttachToResourceRequest({ payload: sagaPayload }: any) {
     const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/document/publicattachtoresource',
            options: {
                body: payload
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();
        if (callback) {
            if (isEmpty(get(res, 'Messages'))) {
                const response = {
                    ...res,
                    IsSuccess: true,
                };

                callback(response);
            }
            else {
                throw new Error('Failed');
            }
        }
    }
    catch (err) {
        yield put(publicAttachToResourceRequestAction(payload, callback));

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchFetchCommunicationResourceResponseDetails() {
    yield takeEvery(
        fetchCommunicationResourceResponseDetailsAction.type,
        handleFetchCommunicationResourceResponseDetails
    );
}
function* watchSendCommunicationResourceResponseRequest() {
    yield takeEvery(
        sendCommunicationResourceResponseAction.type,
        handleSendCommunicationResourceResponseRequest
    );
}
function* watchPublicAttachToResourceRequest() {
    yield takeEvery(
        publicAttachToResourceRequestAction.type,
        handlePublicAttachToResourceRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* conversationLineSaga() {
    yield all([
        fork(watchFetchCommunicationResourceResponseDetails),
        fork(watchSendCommunicationResourceResponseRequest),
        fork(watchPublicAttachToResourceRequest)
    ]);
}

export default conversationLineSaga;
